import { Alert, AlertDescription } from "~/components/ui/alert";
import { PinnedArtifactCard } from "./PinnedArtifactCard";
import { usePinnedArtifacts, usePinnedArtifactMutations } from "./usePinnedArtifacts";

interface PinnedArtifactsProps {
  projectId: string;
  // Whether the caller can pin and unpin, which is owner-only. Refreshing is
  // open to every member.
  canCurate: boolean;
}

// The project's pinned artifacts, rendered for every member.
export const PinnedArtifacts = ({ projectId, canCurate }: PinnedArtifactsProps) => {
  const { pinnedArtifacts, error } = usePinnedArtifacts(projectId);
  const { unpin } = usePinnedArtifactMutations(projectId);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{"Couldn't load pinned artifacts."}</AlertDescription>
      </Alert>
    );
  }
  if (!pinnedArtifacts) return null;
  if (pinnedArtifacts.length === 0) {
    return (
      <p className="rounded-lg border border-border bg-card py-12 text-center text-sm text-muted-foreground">
        No pinned artifacts yet.{" "}
        {canCurate
          ? "Use Pin to project on a chart or table in a session."
          : "Project owners can pin charts and tables from a session."}
      </p>
    );
  }

  return (
    // Two-up only from xl: a chart narrower than ~350px renders as a "view in
    // fullscreen" placeholder instead of the chart.
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
      {pinnedArtifacts.map((pin) => (
        <PinnedArtifactCard
          key={pin.id}
          pin={pin}
          canCurate={canCurate}
          onUnpin={() => void unpin(pin)}
        />
      ))}
    </div>
  );
};
